import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { CategorySelect } from "@/components/categories/CategorySelect";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";

function BudgetCard({ budget: b, spent, onDelete }) {
  const s = spent[b.category] || 0;
  const pct = Math.min(100, Math.round((s / (b.amount || 1)) * 100));
  const over = s > b.amount;
  return (
    <div className="border border-[#D8D4C7] bg-white p-6 hover:border-[#1A1F16] transition-colors">
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">{b.period}</div>
          <h3 className="font-serif text-2xl mt-1">{b.category}</h3>
        </div>
        <button data-testid={`delete-budget-${b.id}`} onClick={() => onDelete(b.id)} className="text-[#6C7565] hover:text-[#D94F3D]"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="num font-serif text-3xl">{formatINR(s)}</div>
      <div className="text-xs text-[#6C7565] font-mono mt-1">of {formatINR(b.amount)} planned</div>
      <div className="h-1.5 bg-[#EAE7DD] mt-4">
        <div className={`h-full ${over ? "bg-[#D94F3D]" : "bg-[#2E6F40]"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between mt-2 font-mono text-[10px] uppercase tracking-widest">
        <span className={over ? "text-[#D94F3D]" : "text-[#6C7565]"}>{pct}% used</span>
        <span className="text-[#6C7565]">{formatINR(Math.max(0, b.amount - s))} left</span>
      </div>
    </div>
  );
}

function BudgetFormModal({ form, setForm, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-md" data-testid="budget-form">
        <h3 className="font-serif text-3xl mb-6">Set a budget</h3>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category</span>
          <CategorySelect type="expense" value={form.category} onChange={(c) => setForm({ ...form, category: c })} testid="budget-category" />
        </label>
        <label className="block mb-4">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Amount (₹)</span>
          <input type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="budget-amount" className="w-full bg-transparent border-b border-[#1A1F16] py-2 text-2xl font-serif num focus:outline-none focus:border-[#A67C00]" />
        </label>
        <label className="block mb-6">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Period</span>
          <select value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="budget-period" className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none">
            <option value="monthly">Monthly</option>
            <option value="weekly">Weekly</option>
            <option value="yearly">Yearly</option>
          </select>
        </label>
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" data-testid="budget-submit" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628]">Save</button>
        </div>
      </form>
    </div>
  );
}

export default function Budgets() {
  const [items, setItems] = useState([]);
  const [spent, setSpent] = useState({});
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({ category: "Groceries", amount: "", period: "monthly" });

  const load = async () => {
    const [b, s] = await Promise.all([api.get("/budgets"), api.get("/summary")]);
    setItems(b.data);
    const map = {};
    s.data.categories.forEach((c) => { map[c.category] = c.amount; });
    setSpent(map);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", { ...form, amount: parseFloat(form.amount) });
      toast.success("Budget set");
      setOpen(false);
      setForm({ category: "Groceries", amount: "", period: "monthly" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = (id) => {
    const b = items.find((x) => x.id === id);
    setConfirm({
      title: "Delete budget?",
      detail: `The ${b?.category || ""} budget of ${formatINR(b?.amount || 0)} will be removed. Transactions stay on the books.`,
      run: async () => { await api.delete(`/budgets/${id}`); toast.success("Removed"); load(); },
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section III</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Budgets.</h2>
        </div>
        <button
          data-testid="add-budget-btn"
          onClick={() => setOpen(true)}
          className="bg-[#1A1F16] text-[#F7F5F0] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#2C3628] flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" /> Set budget
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D8D4C7] bg-white p-12 text-center" data-testid="budgets-empty">
          <p className="font-serif text-2xl text-[#6C7565]">No budgets to keep.</p>
          <p className="text-sm text-[#6C7565] mt-2">Start with one — Groceries is a fine first chapter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="budgets-grid">
          {items.map((b) => <BudgetCard key={b.id} budget={b} spent={spent} onDelete={remove} />)}
        </div>
      )}

      {open && <BudgetFormModal form={form} setForm={setForm} onSubmit={submit} onClose={() => setOpen(false)} />}
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
